import { Formik } from 'formik';
import React, { useMemo } from 'react';
import lodash from 'lodash';
import {
  Button,
  ButtonType,
  Drawer,
  DatePicker,
  Row
} from 'syngenta-digital-cropwise-react-ui-kit';
import moment from 'moment';

import useHasCropZonesInCropSeasons from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useHasCropZonesInCropSeasons';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import { OperationType } from 'components/RecordsListView/constants';
import useOperationEvents from 'hooks/useOperationEvents';
import { FilterFormikData } from 'components/RecordsListView/types';
import defaultInitialValues from './Constants/filterFormikData';
import filtersString from './Constants/filtersString';
import PropertiesFilterSection from './components/PropertiesFilterSection';
import './index.css';

type FilterDrawerProps = {
  visible: boolean;
  initialValues?: FilterFormikData;
  onClose: () => void;
  onFiltersChange?: (filters: FilterFormikData) => void;
  operationType?: OperationType;
};

const { RangePicker } = DatePicker;

const FilterDrawer = ({
  visible,
  initialValues,
  onClose,
  onFiltersChange,
  operationType
}: FilterDrawerProps) => {
  const { sendApplyFiltersEvent } = useOperationEvents(
    operationType ?? OperationType.APPLICATION
  );
  const hasCropZones = useHasCropZonesInCropSeasons();
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();

  const adjustedInitialValues = useMemo(
    () => initialValues ?? defaultInitialValues,
    [initialValues]
  );

  return (
    <Formik<FilterFormikData>
      initialValues={adjustedInitialValues}
      enableReinitialize
      onSubmit={(values, actions) => {
        actions.setTouched({});
        onFiltersChange?.(values);
        actions.setSubmitting(false);
      }}
    >
      {formik => (
        <form onSubmit={formik.handleSubmit}>
          <Drawer
            placement="right"
            open={visible}
            closable={false}
            push={false}
            className="application-filter-drawer"
            data-testid="application-filter-drawer"
            footer={
              <Row justify="space-between">
                <Button
                  type={ButtonType.outline}
                  className="px-3"
                  onClick={() => {
                    formik.resetForm({ values: adjustedInitialValues });
                    onClose();
                  }}
                  size="middle"
                >
                  {filtersString.cancel}
                </Button>
                <Button
                  type={ButtonType.primary}
                  className="px-3"
                  disabled={
                    (lodash.isEqual(formik.values, adjustedInitialValues) &&
                      Object.keys(formik.touched).length === 0) ||
                    Object.keys(formik.errors).length > 0
                  }
                  onClick={() => {
                    sendApplyFiltersEvent(
                      formik.values.dateRange?.length === 2
                    );
                    formik.handleSubmit();
                    onClose();
                  }}
                  size="middle"
                >
                  {filtersString.apply}
                </Button>
              </Row>
            }
          >
            <div className="flex pb-6 px-4">
              <h2 className="text-xl font-semibold">{filtersString.filter}</h2>
              <Button
                type={ButtonType.outline}
                size="small"
                disabled={lodash.isEqual(formik.values, defaultInitialValues)}
                onClick={() => {
                  Object.keys(formik.values).forEach(key => {
                    formik.setFieldTouched(key, true);
                  });
                  formik.setValues(defaultInitialValues);
                }}
                className="ml-auto"
              >
                {filtersString.clearAll}
              </Button>
            </div>

            <div className="flex flex-col border-b border-neutral-20 px-4 pb-5">
              <div className="flex grow py-5">
                <h3 className="font-semibold">{filtersString.dateRange}</h3>
              </div>
              <div
                className="flex grow"
                id="filter-date-range-picker-container"
              >
                <RangePicker
                  name="dateRange"
                  onChange={(dates: [moment.Moment, moment.Moment | null]) => {
                    formik.setFieldValue('dateRange', dates ?? []);
                    formik.setFieldTouched('dateRange', true);
                  }}
                  value={formik.values.dateRange}
                  className="w-full"
                  format="MM/DD/YYYY"
                />
              </div>
            </div>

            <PropertiesFilterSection
              seasonIds={selectedCropSeasonIds}
              hasCropZones={hasCropZones}
            />
          </Drawer>
        </form>
      )}
    </Formik>
  );
};

export default FilterDrawer;
